import { Flex, Img, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Card as CardType } from "../data/types";
import { useStore } from "../store";
import { ReactComponent as PlaySVG } from "../assets/images/play_muteable_color.svg";
import { ReactComponent as NavigationArrow } from "../assets/images/arrowRight.svg";
import { ReactComponent as Closer } from "../assets/images/close_heavy.svg";
import { NavigationOptions } from "swiper/types/components/navigation";
import { usePrevious } from "../hooks/use-previous";
import { RedwoodPalette } from '../data/constants';

const HEIGHT = 215;

export default function ViewPager() {
  const personas = useStore((state) => state.personas);
  const isGalleryMode = useStore((state) => state.isGalleryMode);
  const { width } = useStore((state) => state.windowDimensions);
  const selectedPersona = useStore((state) => state.selectedPersona);
  const setSelectedPersona = useStore((state) => state.setSelectedPersona);
  const prevArrowRef = useRef(null)
  const nextArrowRef = useRef(null)
  const exiterRef = useRef(null)
  const isAnimating = useRef(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const handleSetActiveIndex = useCallback((index: number) => setActiveIndex(index), [])

  // When the end of the swiper gallery is here, allow the user to close.
  const handleEnd = useCallback((e) => {
    e.stopPropagation();
    setSelectedPersona(null);
  }, [setSelectedPersona])


  const cards = useMemo(
    () =>
      selectedPersona
        ? personas.find((p) => p.key === selectedPersona)!.cards
        : [],
    [personas, selectedPersona]
  );



  return (
    <MotionFlex
      pointerEvents="none"
      position="fixed"
      top="0"
      left="0"
      zIndex="2"
    >
      <AnimatePresence exitBeforeEnter>
        {selectedPersona ? (
          <MotionFlex
            key={selectedPersona}
            animate={{
              opacity: 1,
              scale: 1,
              y: "10%", // y was -22% in top position
              transition: { delay: 0.6 },
            }}
            exit={{ opacity: 0, scale: 0, y: "0%" }}
            initial={{ opacity: 0, scale: 0, y: "0%" }}
            width='100vw'
            height='100vh'
            justifyContent="center"
            alignItems="center"
            pointerEvents="none"
            sx={{
              ".swiper-pagination-bullet-active": {
                background: RedwoodPalette.brandNeutral170Alpha,
                color: RedwoodPalette.brandGreen100,
              },
              ".swiper-pagination-bullet": {
                background: RedwoodPalette.brandNeutral170Alpha,
                color: RedwoodPalette.brandNeutral10,
                border: "5px solid " + RedwoodPalette.brandNeutral10,
              },
              ".swiper-pagination-bullets": {
                bottom: "60px",
              },
              ".swiper-pagination-bullets:before": {
                color: RedwoodPalette.brandNeutral10,
                content: "\"\"",
                position: "absolute",
                width: "400px",
                left: 0,
                right: 0,
                margin: '0 auto',
                pointerEvents: "all",
                height: "100px",
                bottom: "-30px",
              },
              ".swiper-pagination": {
                pointerEvents: "all",
                opacity: isGalleryMode ? 0 : 1,
                cursor: 'auto',
              },
              ".swiper-wrapper": {
                alignItems: 'center',
              },
              ".swiper-container": {
                height: 600,
              },
              ".swiper-button-disabled": {
                display: 'none'
              }
            }}
          >
            <Swiper
              grabCursor={true}
              pagination={{ clickable: true }}
              zoom={true}
              centeredSlides={true}
              slidesPerView={'auto'}
              spaceBetween={30}
              observer
              speed={1000}
              updateOnWindowResize
              observeParents
              slideToClickedSlide
              passiveListeners={false}
              onSlideChangeTransitionStart={(swiper) => {
                isAnimating.current = true
              }}
              onSlideChangeTransitionEnd={() => {
                isAnimating.current = false
              }}
              navigation={{
                nextEl: nextArrowRef.current ? nextArrowRef.current : undefined,
                prevEl: prevArrowRef.current ? prevArrowRef.current : undefined
              }}
              onBeforeInit={(swiper) => {
                const navigation = swiper.params!.navigation as NavigationOptions
                navigation.prevEl = prevArrowRef.current;
                navigation.nextEl = nextArrowRef.current;
                swiper.navigation.update();
              }}
              style={{
                pointerEvents: "none",
                paddingBottom: 100,
                paddingTop: 100,
              }}
              className="swiper"
            >
              <MotionFlex
                ref={prevArrowRef}
                position="absolute"
                pointerEvents="all"
                top="50%"
                left="calc(50% - 260px)"
                zIndex="10"
                opacity={isGalleryMode ? 0 : 1}
                cursor="pointer"
                sx={{
                  transform: "translate(-50%, -50%) rotate(180deg)",
                  transition: '.2s transform ease',
                  '&:hover': {
                    transform: "translate(-50%, -50%) rotate(180deg) scale(1.1)",
                  }
                }}
              >
                {activeIndex > 0 && <NavigationArrow height="56px" />}
              </MotionFlex>
              {cards.map((card, index) => (
                <SwiperSlide key={card.name} style={{
                  pointerEvents: "all",
                  width: (width && width > 450) ? "414px" : "90vw",
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {({ isActive, isNext, isPrev }) => (
                    <Suspense fallback={<div>Loading...</div>}>
                      <CardSlide
                        isActive={isActive}
                        isNext={isNext}
                        isPrev={isPrev}
                        card={card}
                        index={index}
                        isAnimating={isAnimating}
                        handleSetActiveIndex={handleSetActiveIndex}
                      />
                    </Suspense>
                  )}
                </SwiperSlide>
              ))}
              <MotionFlex
                ref={nextArrowRef}
                pointerEvents="all"
                position="absolute"
                top="50%"
                left="calc(50% + 260px)"
                transform="translate(-50%, -50%) rotate(0deg)"
                zIndex="10"
                opacity={isGalleryMode ? 0 : 1}
                cursor="pointer"
                sx={{
                  transition: '.2s transform ease',
                  transform: "translate(-50%, -50%) rotate(0deg)",
                  '&:hover': {
                    transform: "translate(-50%, -50%) rotate(0deg) scale(1.1)",
                  }
                }}
              >
                {activeIndex < cards.length - 1 && <NavigationArrow height="56px" />}
              </MotionFlex>
              <MotionFlex
                ref={exiterRef}
                pointerEvents="all"
                position="absolute"
                top="50%"
                left="calc(50% + 260px)"
                transform="translate(-50%, -50%) rotate(0deg)"
                zIndex="11"
                opacity={isGalleryMode ? 0 : 1}
                cursor="pointer"
                onClick={handleEnd}
                sx={{
                  transition: '.2s transform ease',
                  transform: "translate(-50%, -50%) rotate(0deg)",
                  '&:hover': {
                    transform: "translate(-50%, -50%) rotate(0deg) scale(1.1)",
                  }
                }}
              >
                {activeIndex >= cards.length - 1 && <Closer height="45px" />}
              </MotionFlex>
            </Swiper>
          </MotionFlex>
        ) : null}
      </AnimatePresence>
    </MotionFlex>
  );
}

const CardSlide: React.FC<{
  isActive: boolean;
  isNext: boolean;
  isPrev: boolean;
  card: CardType;
  index: number;
  isAnimating: { current: boolean };
  handleSetActiveIndex: (index: number) => void
}> = ({ isActive, isNext, isPrev, card, isAnimating, handleSetActiveIndex, index }) => {
  const [selected, setSelected] = useState(false);
  const isGalleryMode = useStore((state) => state.isGalleryMode);
  const prevGallerMode = usePrevious(isGalleryMode)
  const thumbnails = useStore((state) => state.thumbnails);
  const { width } = thumbnails[card.fullSizeFile] ?? {}
  const setGalleryMode = useStore((state) => state.setGalleryMode);
  const setSelectedCard = useStore((state) => state.setSelectedCard);
  const imageDivRef = useRef<HTMLElement | SVGElement>(null);

  const handleTap = useCallback(() => {
    if (isActive && !isAnimating.current) {
      setSelectedCard(card)
      setSelected(true);
      setGalleryMode(true);
    }
  }, [isActive, isAnimating, setSelectedCard, card, setGalleryMode]);

  useEffect(() => {
    if (isActive) {
      handleSetActiveIndex(index)
    }
  }, [handleSetActiveIndex, index, isActive])

  useEffect(() => {
    if (!isActive && selected) {
      setSelected(false);
      setSelectedCard()
      setGalleryMode(false);
    }
  }, [isActive, selected, setSelectedCard, setGalleryMode]);


  const variants = {
    galleryModeNext: {
      opacity: 0,
      scale: 0.5,
      y: "0",
      zIndex: -1
    },
    galleryModePrev: {
      opacity: 0,
      scale: 0.5,
      y: "0",
      zIndex: -1
    },
    active: {
      opacity: 1,
      scale: 1,
      x: "0%",
      y: '0%',
      transition: {
        delay: prevGallerMode ? 0 : 0.4,
      }
    },
    inViewNext: {
      opacity: 1,
      scale: 0.5,
      x: "0%",
    },
    inViewPrev: {
      opacity: 1,
      scale: 0.5,
      x: "0%",
    },
    hide: {
      opacity: [0, 0],
      scale: [1, 0],
      x: "0%",
    }
  }

  const animateLogic = () => {
    if (isGalleryMode) {
      return isPrev ? 'galleryModePrev' : 'galleryModeNext'
    }

    if (isActive) {
      return 'active'
    }

    if (isPrev) {
      return 'inViewPrev'
    }
    if (isNext) {
      return 'inViewNext'
    }

    return 'hide'
  }

  return (
    <>
      <MotionFlex
        border={
          isActive && isGalleryMode
            ? "2px solid transparent"
            : "2px solid #CCC6C2"
        }
        width="100%"
        flexDirection="column"
        boxShadow={
          isActive && isGalleryMode
            ? "0px 20px 70px 10px rgba(0,0,0, 0.25)"
            : "0px 20px 70px 10px rgba(0,0,0, 0.35)"
        }
        animate={animateLogic()}
        variants={variants}
      >
        <MotionFlex
          ref={imageDivRef}
          height={HEIGHT}
          backgroundImage={`url(${card.thumbnail ? card.thumbnail : card.fullSizeFile})`}
          backgroundPosition="center"
          backgroundSize="cover"
          animate={{
            y: isActive && isGalleryMode ? "50%" : "0%",
            opacity: isGalleryMode ? 0 : 1,
          }}
          cursor="pointer"
          onClick={handleTap}
          pointerEvents={isGalleryMode ? 'none' : 'all'}
          zIndex={isActive && isGalleryMode ? -10 : 10}
        >
          {card.mediaFile && (
            <MotionFlex
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%,-50%)"
              width="84px"
              height="84px"
              zIndex={isGalleryMode && Boolean(card.mediaFile) ? 0 : 3}
            >
              <PlaySVG
                fill={card.mediaPlayButtonColor ? card.mediaPlayButtonColor : "white"}
                className="video-play-button"
              />
            </MotionFlex>
          )}
        </MotionFlex>
        <MotionFlex
          bg="white"
          borderTop="2px solid #CCC6C2"
          p="20px"
          flexDirection="column"
          animate={{ opacity: isGalleryMode && isActive ? 0 : 1 }}
          maxHeight={175}
        >
          {isActive && (
            <Flex>
              {card.momentPath ? (
                <Img
                  width="24px"
                  height="24px"
                  objectFit="contain"
                  mr="10px"
                  src={card.momentPath}
                  alt={card.moment}
                />
              ) : null}
              <Text fontSize="15px">{card.moment}</Text>
            </Flex>
          )}
          <Text fontSize={isActive && width > 400 ? "25px" : "20px"} m={isActive ? "0 0 10px" : "0"} color={RedwoodPalette.neutralGrey190}>
            {card.name}
          </Text>
          {isActive && (
            <MotionText
              overflowY="hidden"
              height="0"
              maxHeight="150px"
              fontSize="12px"
              color={RedwoodPalette.neutralGrey190}
              animate={{
                height: '150px',
                overflowY: 'auto'
              }}
              transition={{
                delay: 0.3,
                duration: 0.5
              }}
              sx={{
                '&::-webkit-scrollbar': {
                  width: 1,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0, .3)',
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0, .3)',
                  borderRadius: 3,
                }
              }}
            >{card.content}</MotionText>
          )}
        </MotionFlex>
      </MotionFlex>
    </>
  );
};

const MotionFlex = motion(Flex);
const MotionText = motion(Text);
